/**
 * Shared "master-compare" exception table builder.
 *
 * One renderer for every "compare an FP record against a master list"
 * exception. Today: helper-products + dosage-name (closed lists, statuses
 * Present | Partial | Missing). Future: COA when it migrates to
 * pwx_masters (open list, statuses Aligned | Mismatch | Missing | Extra).
 *
 * The renderer is field-driven: the backend envelope carries a
 * `compare_config.compare_fields` list (e.g. handle + name for products,
 * code + name + type + class + tax_type for COA) and each row carries
 * matching `expected.{key}` / `found.{key}` / `field_matches.{key}`
 * payload. The same column shape works for both kinds; only the
 * `statusSet` differs.
 *
 * Column shape (inspector context):
 *   Issues | Status | Master | Found in {System} | Issue | Action
 * (the Issues chip is the standard tag-backed rollup + drawer trigger;
 * pass ``issuesColumn: false`` to omit it - Xero COA does, pending
 * drawer-resolution support for COA accounts)
 *
 * Default view: "Needs attention only" toggle ON - Present / Aligned rows
 * are hidden until the user flips the toggle off.
 */
import React from 'react'
import { Box, Chip, Tooltip, Typography } from '@mui/material'
import { withTooltip, typographyEl } from './muiElements'
import { buildIssuesColumn } from './issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from './platformColumns'
import { getRowSummaryTitle } from '../../../utils/exceptionSummary'
import { composeRowSubtitle, textPart } from './rowSubtitle'
import {
  renderCategoryNameWithMeta,
  renderProductNameWithMeta,
} from './productRenderers'
import type {
  ExceptionConfig,
  ExceptionColumnDef,
  RenderContext,
} from '../types'

export type CompareStatusSet = 'closed' | 'open'

export interface CompareField {
  key: string
  label: string
}

export interface BuildMasterCompareConfigArgs {
  /** Exception code(s) this config handles. */
  codes: string[]
  /** Closed = Present/Partial/Missing. Open = Aligned/Mismatch/Missing/Extra. */
  statusSet: CompareStatusSet
  /** Fields to render side-by-side in Master / Found cells. */
  compareFields: CompareField[]
  /** Label used in the "Found in {System}" header, e.g. "Lightspeed", "Xero". */
  systemLabel: string
  /** Override the row key (defaults to ``object_id``). */
  rowKeyField?: string
  /**
   * Include the shared Issues column (default true). Compare rows are
   * lifecycle-tagged objects too, so the chip surfaces every open tag on
   * the object (family-wide) and opens the per-row drawer. Xero COA opts
   * out until its drawer resolution is proven.
   */
  issuesColumn?: boolean
  /** Empty-state message shown when there are no rows for this exception. */
  emptyMessage?: string
  /** Hide the optional Action column. Default: shown. */
  hideActionColumn?: boolean
  /**
   * Compare field whose value is identical to ``master_value`` (the
   * master-list "join key"). Suppresses the ``Expected <Label>`` column
   * in the platform context so the operator doesn't see two columns with
   * the same value. The ``Found <Label>`` column stays - it can diverge
   * (null for Missing, different value for Extra). Example: COA's join
   * key is ``code`` so ``Expected Code`` is hidden but ``Found Code``
   * remains to surface Extra account codes.
   */
  masterKeyField?: string
  /**
   * Explicit ordered list of status keys to include in the summary-chip
   * strip + the chip-filter default. Overrides the ``statusSet``-default
   * categories. Use when a domain has its own taxonomy (e.g. LS category
   * alignment uses ``near_match`` instead of the generic ``mismatch``).
   * Default needs-attention selection = every key minus present / aligned.
   */
  statusKeys?: string[]
  /** Extra columns appended after the framework's 5-column shape. */
  extraInspectorColumns?: ExceptionColumnDef[]
  extraPlatformColumns?: ExceptionColumnDef[]
}

interface StatusDescriptor {
  label: string
  pillBg: string
  pillFg: string
  sortRank: number
  rowTint?: string
}

// Compare-table palette. Row tints are intentionally near-white so the
// status pill + Issue column carry the alert and the row stays calm.
// Mismatch lives only on the changed value (small pill); the cell as a
// whole never tints red.
const ROW_TINT_MISSING = '#F6FBFD'
const ROW_TINT_MISMATCH = '#FFF8FA'
const ROW_TINT_PARTIAL = '#FFFBF4'
const ROW_TINT_EXTRA = '#FFFBF4'

// Soft rose mismatch indicator. Inline pill around the differing value
// only - same palette as the sales-compare delta chip, but no arrow /
// numeric value (those don't translate to string compares). Tooltip on
// hover carries the expected-vs-found detail.
const MISMATCH_PILL_BG = 'rgba(244, 67, 54, 0.10)'

// Status pill palette - filled pale tint + bolder matching text. Calmer
// than the outlined statusPillChipProps used elsewhere; intentionally
// scoped to the compare table so other surfaces keep their existing pill.
const STATUS_TABLE: Record<string, StatusDescriptor> = {
  present: {
    label: 'Present',
    pillBg: '#E7F6EC',
    pillFg: '#166534',
    sortRank: 4,
  },
  aligned: {
    label: 'Aligned',
    pillBg: '#E7F6EC',
    pillFg: '#166534',
    sortRank: 4,
  },
  partial: {
    label: 'Partial',
    pillBg: '#FEF3C7',
    pillFg: '#92400E',
    sortRank: 1,
    rowTint: ROW_TINT_PARTIAL,
  },
  mismatch: {
    label: 'Mismatch',
    pillBg: '#FEE2E2',
    pillFg: '#B91C1C',
    sortRank: 0,
    rowTint: ROW_TINT_MISMATCH,
  },
  near_match: {
    label: 'Near match',
    pillBg: '#FEF3C7',
    pillFg: '#92400E',
    sortRank: 1,
    rowTint: ROW_TINT_PARTIAL,
  },
  missing: {
    label: 'Missing',
    pillBg: '#E0F2F4',
    pillFg: '#0E7490',
    sortRank: 2,
    rowTint: ROW_TINT_MISSING,
  },
  extra: {
    label: 'Extra',
    pillBg: '#FEF3C7',
    pillFg: '#92400E',
    sortRank: 3,
    rowTint: ROW_TINT_EXTRA,
  },
}

// Typography tokens used inside the compare cells. Sized so that primary
// identifier + name + metadata bands all read as the same font family at
// a clean stack of weights/sizes - the original layout drifted into
// "different font systems" territory because the bands were tuned
// independently. Tokens here are the single source of truth.
const PRIMARY_TEXT = '#111827'
const NAME_TEXT = '#374151'
const SECONDARY_TEXT = '#6B7280'
const EMPTY_SIDE_DASH_COLOR = '#9CA3AF'
// Teal "Likely match" hint used inside the Issue cell only - the rest of
// the compare cells stay neutral so the Status pill and Issue text carry
// the alert.
const LIKELY_HINT_FG = '#008A93'

const PRESENT_LIKE = new Set(['present', 'aligned'])

const renderStatusChip = (value: any): React.ReactNode => {
  const descriptor = STATUS_TABLE[value]
  if (!descriptor) {
    return React.createElement(Chip, { size: 'small', label: String(value ?? '-') })
  }
  return React.createElement(Chip, {
    label: descriptor.label,
    size: 'small',
    sx: {
      height: 20,
      borderRadius: 999,
      fontSize: '0.72rem',
      fontWeight: 600,
      letterSpacing: '0.01em',
      color: descriptor.pillFg,
      backgroundColor: descriptor.pillBg,
      border: 'none',
      '& .MuiChip-label': { px: 0.9, py: 0 },
    },
  })
}

// Row tint is applied at the row level (via `config.getRowBgColor`) so
// every cell - including extras appended by callers - inherits the
// tint and the hover state without each column having to opt in.
const rowTintFor = (row: Record<string, any>): string | undefined =>
  STATUS_TABLE[row.alignment_status]?.rowTint

// Per-row vertical padding. COA-style rows render three bands per side
// (code / name / metadata) and need a little breathing room; the
// helper-products / dosage-name / category-alignment tables stay
// compact because each side renders at most two lines. Driven by
// `compareFields.length` so the same renderer covers both densities.
const verticalPaddingFor = (compareFields: CompareField[]): string =>
  compareFields.length >= 3 ? '11px' : '8px'

// Build a per-cell sx that applies the row's vertical alignment +
// padding. The `placement` arg lets short status/issue/action cells sit
// vertically centred against the (taller) Master / Found stacks so
// the row feels balanced rather than top-heavy.
const cellSxFor = (
  compareFields: CompareField[],
  placement: 'top' | 'middle',
): Record<string, any> => {
  const py = verticalPaddingFor(compareFields)
  return {
    verticalAlign: placement,
    paddingTop: py,
    paddingBottom: py,
  }
}

const sortRankFor = (row: Record<string, any>): number => {
  const descriptor = STATUS_TABLE[row.alignment_status]
  return descriptor?.sortRank ?? 99
}

// Mono is only used when the field actually IS a code-like identifier
// (handles, codes, SKUs). Category alignment's only field is a name, so
// it should render in the regular UI font even though it sits at idx 0.
const CODE_LIKE_KEYS = new Set(['handle', 'code', 'sku'])

const isCodeLike = (key: string): boolean => CODE_LIKE_KEYS.has(key)

const monoFamily = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

// Primary (idx 0) - bold, near-black. Mono if the field is itself a code
// (handles, codes, SKUs) so identifiers read as identifiers regardless
// of band position.
const idStyleFor = (key: string): Record<string, any> => ({
  display: 'inline',
  fontSize: '0.8125rem',
  fontWeight: 600,
  lineHeight: 1.35,
  color: PRIMARY_TEXT,
  fontFamily: isCodeLike(key) ? monoFamily : undefined,
})

// Secondary (idx 1) - regular weight, neutral grey. Same code-like rule
// as the primary so a SKU dropped to the secondary line stays mono and
// keeps reading as a code rather than a name.
const secondaryStyleFor = (key: string): Record<string, any> => ({
  display: 'inline',
  fontSize: '0.8125rem',
  fontWeight: 400,
  lineHeight: 1.35,
  color: NAME_TEXT,
  fontFamily: isCodeLike(key) ? monoFamily : undefined,
})

const metaSegmentStyle: Record<string, any> = {
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: 1.35,
  color: SECONDARY_TEXT,
}

const friendlyDifferenceTooltip = (
  fieldLabel: string,
  expectedValue: string | null | undefined,
  foundValue: string | null | undefined,
): string => {
  const label = fieldLabel.toLowerCase()
  return `Expected ${label}: ${expectedValue || '-'}. Found: ${foundValue || '-'}.`
}

// Inline rose pill wrapping the differing value. Same palette as the
// sales-compare delta chip, applied as a soft background only (no
// border, no arrow, no text colour change) so the Found column reads
// as neutral text with one or two flagged tokens rather than "the row
// is on fire". Tooltip on hover carries expected-vs-found detail.
const mismatchPillSx: Record<string, any> = {
  backgroundColor: MISMATCH_PILL_BG,
  borderRadius: '4px',
  paddingLeft: '4px',
  paddingRight: '4px',
  cursor: 'help',
}

/**
 * Render a stacked compare cell. Layout:
 *   line 1 (idx 0)     - identifier - monospace if code-like
 *   line 2 (idx 1)     - name
 *   line 3 (idx >= 2)  - metadata compacted onto one ` · `-joined line
 *
 * Empty side -> one muted dash. Differing fields on the Found side wear
 * a soft inline rose pill around the value with a hover tooltip giving
 * the expected/found detail; the Master side never wears the pill
 * because there's nothing to "fix" on it. Mismatch markers only render
 * when the row has at least one matching field - Extra and
 * Missing-with-fuzzy rows are record-level signals, not per-field
 * diffs, so the Status pill + Issue column carry them.
 */
const renderCompareCell = (
  side: 'expected' | 'found',
  compareFields: CompareField[],
) => (_value: any, row: Record<string, any>): React.ReactNode => {
  const data = (row[side] || {}) as Record<string, string | null>
  const sideHasAnyValue = compareFields.some((f) => {
    const v = data[f.key]
    return v !== null && v !== undefined && v !== ''
  })

  if (!sideHasAnyValue) {
    return typographyEl(
      {
        component: 'span',
        sx: { color: EMPTY_SIDE_DASH_COLOR, fontSize: '0.8125rem' },
      },
      '-',
    )
  }

  const fieldMatches = (row.field_matches || {}) as Record<string, boolean>
  // Only mark per-field differences when at least one field IS matching.
  // All-false (Extra, missing-with-fuzzy) is a record-level signal, not a
  // per-field diff - per-row chip + Issue column carry it.
  const recordIsComparable =
    side === 'found' &&
    compareFields.some((f) => fieldMatches[f.key] === true)

  const children: React.ReactNode[] = []
  const metaSegments: React.ReactNode[] = []

  compareFields.forEach((field, idx) => {
    const myValue = data[field.key]
    if (myValue === null || myValue === undefined || myValue === '') return

    const expectedValue = (row.expected || {})[field.key]
    const isMatch = fieldMatches[field.key] !== false
    const showAsMismatch = recordIsComparable && !isMatch

    if (idx >= 2) {
      // Metadata band. Each token renders as its own span so we can
      // wrap just the differing token in the rose pill while
      // neighbouring tokens stay neutral. Metadata stays unlinked -
      // only the primary identifier and name are clickable.
      const seg = typographyEl(
        {
          key: `${field.key}-seg`,
          component: 'span',
          sx: { ...metaSegmentStyle, ...(showAsMismatch ? mismatchPillSx : null) },
        },
        String(myValue),
      )
      metaSegments.push(
        showAsMismatch
          ? withTooltip(
              {
                key: `${field.key}-tip`,
                title: friendlyDifferenceTooltip(field.label, expectedValue, myValue),
                placement: 'top',
                arrow: true,
              },
              seg,
            )
          : seg,
      )
      return
    }

    const baseStyle =
      idx === 0 ? idStyleFor(field.key) : secondaryStyleFor(field.key)
    const valueNode = typographyEl(
      {
        key: `${field.key}-val`,
        component: 'span',
        sx: { ...baseStyle, ...(showAsMismatch ? mismatchPillSx : null) },
      },
      String(myValue),
    )
    const wrappedValue = showAsMismatch
      ? withTooltip(
          {
            title: friendlyDifferenceTooltip(field.label, expectedValue, myValue),
            placement: 'top',
            arrow: true,
          },
          valueNode,
        )
      : valueNode

    children.push(
      React.createElement(
        Box,
        { key: `${field.key}-block`, sx: { display: 'block' } },
        wrappedValue,
      ),
    )
  })

  if (metaSegments.length > 0) {
    const dotted: React.ReactNode[] = []
    metaSegments.forEach((seg, i) => {
      if (i > 0) {
        dotted.push(
          typographyEl(
            {
              key: `sep-${i}`,
              component: 'span',
              sx: { ...metaSegmentStyle, color: EMPTY_SIDE_DASH_COLOR, mx: 0.5 },
            },
            '·',
          ),
        )
      }
      dotted.push(seg)
    })
    children.push(
      React.createElement(
        Box,
        { key: 'meta-band', sx: { display: 'block', mt: '2px' } },
        ...dotted,
      ),
    )
  }

  return React.createElement(Box, { sx: { minWidth: 0 } }, ...children)
}

/**
 * Render the Issue/Difference cell. Plain text in nearly every case -
 * the inline rose pill on the differing value inside the Found cell
 * already carries the visual diff. The only special case is "Likely
 * match NN%" (fuzzy candidate above threshold) which renders in teal
 * with a tooltip nudge to verify before acting.
 */
const renderIssueCellFor = (_compareFields: CompareField[]) =>
  (_value: any, row: Record<string, any>): React.ReactNode => {
  const text: string = row.issue_text || ''
  if (!text) {
    return typographyEl(
      { component: 'span', sx: { color: EMPTY_SIDE_DASH_COLOR, fontSize: '0.8125rem' } },
      '-',
    )
  }
  const isLikely = text.toLowerCase().startsWith('likely')
  const labelNode = typographyEl(
    {
      component: 'span',
      sx: {
        fontSize: '0.8125rem',
        lineHeight: 1.35,
        color: isLikely ? LIKELY_HINT_FG : NAME_TEXT,
        fontWeight: isLikely ? 600 : 500,
        cursor: isLikely ? 'help' : 'default',
      },
    },
    text,
  )
  if (!isLikely) return labelNode
  return withTooltip(
    {
      title: 'Likely match based on similar product name. Please review before changing.',
      placement: 'top',
      arrow: true,
    },
    labelNode,
  )
}

// Path picks off compareFields shape: handle/sku → product renderer, name-only → category renderer.
const LS_NO_MATCH_FG = '#9CA3AF'

const renderFoundInLightspeedCell = (compareFields: CompareField[]) =>
  (_value: any, row: Record<string, any>, ctx: RenderContext): React.ReactNode => {
  const data = (row.found || {}) as Record<string, string | null>
  const link = (row.match_link as string | null | undefined) ?? null

  const nameField = compareFields.find((f) => f.key === 'name')
  const skuField = compareFields.find(
    (f) => f.key === 'handle' || f.key === 'sku',
  )
  const nameValue = nameField ? data[nameField.key] : null
  const skuValue = skuField ? data[skuField.key] : null

  const hasAnyValue =
    (nameValue !== null && nameValue !== undefined && nameValue !== '') ||
    (skuValue !== null && skuValue !== undefined && skuValue !== '')

  if (!hasAnyValue) {
    const noun = skuField ? 'product' : 'category'
    return typographyEl(
      {
        component: 'span',
        sx: { color: LS_NO_MATCH_FG, fontSize: '0.8125rem' },
      },
      `No ${noun} found`,
    )
  }

  const fieldMatches = (row.field_matches || {}) as Record<string, boolean>
  const nameMismatch =
    nameField !== undefined && fieldMatches[nameField.key] === false
  const skuMismatch =
    skuField !== undefined && fieldMatches[skuField.key] === false

  if (skuField) {
    const productLikeRow: Record<string, any> = {
      lightspeed_product_link: link,
      sku: skuValue,
      product_type: row.product_type,
      is_composite: row.is_composite,
      has_variants: row.has_variants,
      variant_parent_id: row.variant_parent_id,
    }
    return renderProductNameWithMeta(nameValue ?? '-', productLikeRow, ctx, {
      showCategory: false,
      showInactiveChip: false,
      linkColor: 'inherit',
      nameMismatch,
      skuMismatch,
    })
  }

  // Category path: folder icon + linked name pointing at the LS category edit page.
  const categoryLikeRow: Record<string, any> = {
    lightspeed_category_link: link,
  }
  return renderCategoryNameWithMeta(nameValue ?? '-', categoryLikeRow, ctx, {
    linkColor: 'inherit',
    nameMismatch,
  })
}

// Platform-context "Found <Field>" cell renderer. Colours the value red
// when ``row.field_matches[fieldKey] === false`` AND there's an expected
// sibling to compare against - so an operator scanning the export sees
// the mismatched cell at a glance. Skipped on Extra rows (no expected
// side; Status pill + Issue text already signal those).
//
// Returns ``undefined`` for the happy path so the cell falls through to
// ``DataViewTable``'s default ``formatCellValue`` rendering. That
// keeps the font identical to every other column in the row - wrapping
// non-mismatched cells in a custom Typography would override the
// table's default text style and the column would visibly stand out
// even when there's nothing wrong with the value.
const renderFoundCompareCell = (fieldKey: string) =>
  (value: any, row: Record<string, any>): React.ReactNode | undefined => {
    const isEmpty = value === null || value === undefined || value === ''
    if (isEmpty) return undefined
    const expected = row?.[`expected_${fieldKey}`]
    const hasExpected = expected !== null && expected !== undefined && expected !== ''
    const matchFlag = row?.field_matches?.[fieldKey]
    const mismatched = hasExpected && matchFlag === false
    if (!mismatched) return undefined
    return React.createElement(
      'span',
      { style: { color: '#B91C1C', fontWeight: 500 } },
      String(value),
    )
  }


const renderActionCell = (_value: any, row: Record<string, any>): React.ReactNode => {
  const hint = row.action_hint
  if (!hint) {
    return typographyEl(
      { component: 'span', sx: { color: EMPTY_SIDE_DASH_COLOR, fontSize: '0.78rem' } },
      '-',
    )
  }
  return typographyEl(
    {
      component: 'span',
      sx: {
        fontSize: '0.78rem',
        lineHeight: 1.35,
        color: SECONDARY_TEXT,
        fontWeight: 400,
      },
    },
    hint,
  )
}

const NEEDS_ATTENTION_KEYS_BY_STATUS_SET: Record<CompareStatusSet, string[]> = {
  closed: ['missing', 'partial'],
  open: ['mismatch', 'missing', 'extra'],
}

const DEFAULT_KEYS_BY_STATUS_SET: Record<CompareStatusSet, string[]> = {
  closed: ['missing', 'partial', 'present'],
  open: ['mismatch', 'missing', 'extra', 'aligned'],
}

const STATUS_KEY_TO_CHIP_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  present: 'success',
  aligned: 'success',
  partial: 'warning',
  near_match: 'warning',
  extra: 'warning',
  mismatch: 'error',
  missing: 'info',
}

const buildSummaryChips = (
  statusSet: CompareStatusSet,
  statusKeys?: string[],
) => {
  const keys = statusKeys ?? DEFAULT_KEYS_BY_STATUS_SET[statusSet]
  const defaultActiveKeys = statusKeys
    ? statusKeys.filter((k) => !PRESENT_LIKE.has(k))
    : NEEDS_ATTENTION_KEYS_BY_STATUS_SET[statusSet]
  const categories = keys.map((key) => {
    const descriptor = STATUS_TABLE[key]
    return {
      key,
      label: descriptor?.label ?? key,
      color: STATUS_KEY_TO_CHIP_COLOR[key] ?? 'default',
      countField: key,
    }
  })
  return {
    statusField: 'alignment_status',
    countsKey: 'counts',
    multiSelect: true as const,
    defaultActiveKeys,
    categories,
  }
}

/**
 * Top-level factory. Each compare-style exception calls this once.
 */
export function buildMasterCompareConfig(
  args: BuildMasterCompareConfigArgs,
): ExceptionConfig {
  const {
    codes,
    statusSet,
    compareFields,
    systemLabel,
    rowKeyField = 'object_id',
    emptyMessage,
    hideActionColumn = false,
    issuesColumn = true,
    statusKeys,
    extraInspectorColumns,
    extraPlatformColumns,
    masterKeyField,
  } = args

  const expectedRenderer = renderCompareCell('expected', compareFields)
  // Lightspeed-targeted master lists get the new product-suggestion
  // treatment (clickable name, reason pill, SKU underneath). Xero COA
  // keeps the original side-by-side renderer because the framing
  // (single linked LS resource per row) doesn't translate to a COA
  // account compare with 5 metadata bands.
  const foundRenderer =
    systemLabel === 'Lightspeed'
      ? renderFoundInLightspeedCell(compareFields)
      : renderCompareCell('found', compareFields)
  const issueRenderer = renderIssueCellFor(compareFields)

  // Status / Issue / Suggested fix are typically a single chip or one
  // line of text - vertically centre them against the (taller) Master
  // and Found stacks so the row feels balanced. Master and Found
  // themselves keep top-alignment because their stacked bands need to
  // start from a consistent line.
  const stackedCellSx = cellSxFor(compareFields, 'top')
  const singleLineCellSx = cellSxFor(compareFields, 'middle')

  const inspectorColumns: ExceptionColumnDef[] = [
    // Issues first, matching every other inspector exception config -
    // compare rows are lifecycle-tagged objects, so the chip rolls up the
    // object's open tags (family-wide) and is the drawer trigger.
    ...(issuesColumn ? [buildIssuesColumn()] : []),
    {
      key: 'alignment_status',
      label: 'Status',
      contexts: ['inspector'],
      render: renderStatusChip,
      getSortValue: sortRankFor,
      getCellStyle: () => singleLineCellSx,
    },
    {
      key: 'master_value',
      label: 'Master',
      contexts: ['inspector'],
      align: 'left',
      render: expectedRenderer,
      getCellStyle: () => stackedCellSx,
    },
    {
      key: 'found',
      label: `Found in ${systemLabel}`,
      contexts: ['inspector'],
      align: 'left',
      render: foundRenderer,
      getCellStyle: () => stackedCellSx,
    },
    {
      key: 'issue_text',
      label: 'Issue',
      contexts: ['inspector'],
      align: 'left',
      render: issueRenderer,
      getCellStyle: () => singleLineCellSx,
    },
  ]

  if (!hideActionColumn) {
    inspectorColumns.push({
      key: 'action_hint',
      label: 'Suggested fix',
      contexts: ['inspector'],
      align: 'left',
      render: renderActionCell,
      getCellStyle: () => singleLineCellSx,
    })
  }

  const platformColumns: ExceptionColumnDef[] = [
    ...PLATFORM_CONTEXT_COLUMNS,
    {
      key: 'alignment_status',
      label: 'Status',
      contexts: ['platform'],
      render: renderStatusChip,
      getSortValue: sortRankFor,
    },
    {
      key: 'master_value',
      label: 'Master Value',
      contexts: ['platform'],
      align: 'left',
    },
    ...compareFields.flatMap<ExceptionColumnDef>((field) => {
      // The master-key field's "Expected" cell is byte-identical to
      // ``master_value`` (it's the join key); hide it so the operator
      // doesn't read the same value twice. The "Found" cell still goes
      // in - it can be null on Missing or carry the FP-side value on
      // Extra / Mismatch.
      const cols: ExceptionColumnDef[] = []
      if (field.key !== masterKeyField) {
        cols.push({
          key: `expected_${field.key}`,
          label: `Expected ${field.label}`,
          contexts: ['platform'],
          align: 'left',
        })
      }
      cols.push({
        key: `found_${field.key}`,
        label: `Found ${field.label}`,
        contexts: ['platform'],
        align: 'left',
        render: renderFoundCompareCell(field.key),
      })
      return cols
    }),
    // Free-text Issue then Suggested fix at the end, just before any
    // per-code extras (Similarity for category-alignment + helper/dosage).
    // Keeps the narrative cells after the structured identity columns.
    {
      key: 'issue_text',
      label: 'Issue',
      contexts: ['platform'],
      align: 'left',
    },
    {
      key: 'action_hint',
      label: 'Suggested fix',
      contexts: ['platform'],
      align: 'left',
    },
  ]

  return {
    codes,
    columns: [
      ...inspectorColumns,
      ...(extraInspectorColumns ?? []),
      ...platformColumns,
      ...(extraPlatformColumns ?? []),
    ],
    defaultSortColumn: 'alignment_status',
    defaultSortDirection: 'asc',
    complexity: 'simple',
    rowKeyField,
    emptyMessage,
    summaryChips: buildSummaryChips(statusSet, statusKeys),
    // Apply the row tint at the row level so extra columns appended by
    // callers (e.g. the "# Products" column on category alignment)
    // inherit it without each column having to opt in via getCellStyle.
    getRowBgColor: rowTintFor,
    // Comments side-panel: prefer the per-row ``issue_text`` (already
    // a human-readable one-liner like "Name differs" or "Likely match
    // 88%") falling through to the master value. Avoids dumping the
    // raw ``object_id`` (which is the synthetic key like
    // ``missing-helper-products:sia-lacallout``) into the panel
    // header.
    getRowTitle: (row) => {
      const issue =
        typeof row.issue_text === 'string' ? row.issue_text.trim() : ''
      const masterValue =
        typeof row.master_value === 'string' ? row.master_value.trim() : ''
      if (issue && masterValue) return `${masterValue} - ${issue}`
      if (issue) return issue
      if (masterValue) return masterValue
      return getRowSummaryTitle(row)
    },
    // Subtitle surfaces the alignment status + FP-side product count when
    // available. Compare configs aggregate per-master row (one row per
    // helper / dosage / category) so a "12 products" hint tells the
    // reviewer the scope before they click in.
    getRowSubtitle: (row) =>
      composeRowSubtitle([
        textPart(row.alignment_status_label) || textPart(row.alignment_status),
        typeof row.product_count === 'number' && row.product_count > 0
          ? `${row.product_count} product${row.product_count === 1 ? '' : 's'}`
          : null,
      ]),
  }
}
